import { useTranslation } from "react-i18next";

import {
  ISoccerCommentaryMessage,
  SoccerMatchCommentaryEventType,
  SupportedSoccerCommentaryEvent,
} from "../../../../../types";
import TimeSvg from "../../../assets/icons/timer.svg";

// Map event types to their respective image URLs
const eventImageMap: Record<SupportedSoccerCommentaryEvent, string> = {
  [SoccerMatchCommentaryEventType.ADDED_TIME]:
    "/images/match-events/sbs-added-time.svg",
  [SoccerMatchCommentaryEventType.ATTEMPT_BLOCKED]:
    "/images/match-events/sbs-attempt-blocked.svg",
  [SoccerMatchCommentaryEventType.ATTEMPT_SAVED]:
    "/images/match-events/sbs-attempt-saved.svg",
  [SoccerMatchCommentaryEventType.CORNER]:
    "/images/match-events/sbs-corner.svg",
  [SoccerMatchCommentaryEventType.END_PERIOD]:
    "/images/match-events/sbs-added-time.svg",
  [SoccerMatchCommentaryEventType.END_DELAY]:
    "/images/match-events/sbs-end-delay.svg",
  [SoccerMatchCommentaryEventType.FREE_KICK_LOST]:
    "/images/match-events/sbs-free-kick-lost.svg",
  [SoccerMatchCommentaryEventType.FREE_KICK_WON]:
    "/images/match-events/sbs-free-kick-won.svg",
  [SoccerMatchCommentaryEventType.YELLOW_CARD]:
    "/images/match-events/yellow-card.svg",
  [SoccerMatchCommentaryEventType.RED_CARD]:
    "/images/match-events/red-card.svg",
  [SoccerMatchCommentaryEventType.SECOND_YELLOW_RED_CARD]:
    "/images/match-events/second-yellow-card.svg",
  [SoccerMatchCommentaryEventType.GOAL]:
    "/images/match-events/sbs-goal.svg",
  [SoccerMatchCommentaryEventType.LINEUP]:
    "/images/match-events/sbs-lineup.svg",
  [SoccerMatchCommentaryEventType.MISS]:
    "/images/match-events/sbs-miss.svg",
  [SoccerMatchCommentaryEventType.OFFSIDE]:
    "/images/match-events/sbs-offside.svg",
  [SoccerMatchCommentaryEventType.OWN_GOAL]:
    "/images/match-events/sbs-goal.svg",
  [SoccerMatchCommentaryEventType.PENALTY_GOAL]:
    "/images/match-events/sbs-penalty-goal.svg",
  [SoccerMatchCommentaryEventType.PENALTY_LOST]:
    "/images/match-events/sbs-penalty-lost.svg",
  [SoccerMatchCommentaryEventType.PENALTY_MISS]:
    "/images/match-events/sbs-penalty-goal.svg",
  [SoccerMatchCommentaryEventType.PENALTY_SAVED]:
    "/images/match-events/sbs-penalty-saved.svg",
  [SoccerMatchCommentaryEventType.PENALTY_WON]:
    "/images/match-events/sbs-penalty-won.svg",
  [SoccerMatchCommentaryEventType.PLAYER_RETIRED]:
    "/images/match-events/sbs-player-retired.svg",
  [SoccerMatchCommentaryEventType.POST]:
    "/images/match-events/sbs-post.svg",
  [SoccerMatchCommentaryEventType.POSTPONED]:
    "/images/match-events/sbs-postponed.svg",
  [SoccerMatchCommentaryEventType.START_PERIOD]:
    "/images/match-events/sbs-end-delay.svg",
  [SoccerMatchCommentaryEventType.START_DELAY]:
    "/images/match-events/sbs-start-delay.svg",
  [SoccerMatchCommentaryEventType.SUBSTITUTION]:
    "/images/match-events/sbs-substitution.svg",
  [SoccerMatchCommentaryEventType.CONTENTIOUS_REFEREE_DECISIONS]:
    "/images/match-events/sbs-var-decision.svg",
  [SoccerMatchCommentaryEventType.VAR_CANCELLED_GOAL]:
    "/images/match-events/sbs-var-cancelled-goal.svg",
  [SoccerMatchCommentaryEventType.DELETED_AFTER_REVIEW]:
    "/images/match-events/sbs-deleted-after-review.svg",
};

export function LiveCommentaryMessage({
  message,
}: {
  message: ISoccerCommentaryMessage;
}) {
  const { t } = useTranslation();

  // END_PERIOD arrives as "end 1", "end 2", etc. — normalize for icon lookup.
  const lookupType = message.type.startsWith(
    SoccerMatchCommentaryEventType.END_PERIOD + " "
  )
    ? SoccerMatchCommentaryEventType.END_PERIOD
    : message.type;
  const imageUrl =
    eventImageMap[lookupType as SupportedSoccerCommentaryEvent] || "";

  const commentaryTimeText = (() => {
    if (message.type.startsWith(SoccerMatchCommentaryEventType.END_PERIOD)) {
      return t("gameStatus.fullTime");
    }
    if (message.type === SoccerMatchCommentaryEventType.START_PERIOD) {
      return t("gameStatus.start");
    }
    return "";
  })();

  const matchTime = message.time?.trim();
  const displayTime = matchTime || commentaryTimeText;
  return (
    <div className="commentary-message">
      {/* Time box */}
      <div className="commentary-time-box">
        {displayTime ? (
          <p className="commentary-time">
            {matchTime && <img src={TimeSvg} alt="commentary time" />}
            {displayTime}
          </p>
        ) : (
          <div className="placeholder" />
        )}
      </div>

      {/* Image box */}
      <div className="commentary-img-box">
        {imageUrl ? (
          <div className="commentary-img">
            <img src={imageUrl} alt="match event" />
          </div>
        ) : (
          <div className="placeholder" />
        )}
      </div>
      <div className="commentary-text-box ">
        {/* Text */}
        <p className="commentary-text">{message.comment}</p>
      </div>
    </div>
  );
}
